import * as Sentry from "@sentry/react";
import { onlineManager } from "@tanstack/react-query";

import { isOnline } from "@/offline/online-status";

/**
 * Keeps a Sentry `connectivity` tag (`online` | `offline`) in sync with the
 * device's network state, so filtering Sentry by `connectivity:offline`
 * surfaces every error that happened in the field — see
 * `specs/conventions/error-reporting.md`.
 *
 * Value source: `isOnline()` from `@/offline/online-status`, the SAME
 * `navigator.onLine` read that `apiFetch` uses to decide whether to throw
 * `OfflineError`. Reading the tag from the same source keeps it consistent
 * with `OfflineError` semantics — a `connectivity:offline` event is exactly
 * one where an `apiFetch` would have short-circuited offline.
 *
 * Change source: TanStack Query's `onlineManager`, which listens to the
 * `online` / `offline` window events — the same events that flip
 * `navigator.onLine`. Subscribing to it (rather than wiring our own window
 * listeners) gives a single, already-managed notification stream; the tag
 * VALUE is still re-read from `isOnline()` on every change so it never
 * drifts from `OfflineError` semantics (`onlineManager` deliberately no
 * longer reads `navigator.onLine`, so it cannot be trusted as the value).
 */
function applyConnectivityTag(): void {
  Sentry.getGlobalScope().setTag(
    "connectivity",
    isOnline() ? "online" : "offline",
  );
}

/**
 * Sets the `connectivity` tag on Sentry's global scope (so it applies to
 * every event) and keeps it updated as connectivity changes. Idempotent
 * enough for a single call from `instrument.ts` after `Sentry.init()`.
 *
 * @returns an unsubscribe function that detaches the connectivity listener.
 */
export function initConnectivityTag(): () => void {
  applyConnectivityTag();
  return onlineManager.subscribe(() => {
    applyConnectivityTag();
  });
}
